import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import Cover from "./assets/password-cover.svg";
import Panel from "./assets/password-panel.svg";
import ConfirmButton from "./assets/password-confirm-button.svg";
import WhiteLogo from "./assets/white-logo.svg";

const StyledDiv = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  height: 100vh;
  width: 100%;
  background-image: url(${Cover});
  background-size: 100%;
  background-repeat: repeat-y;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30vh 0;
  .logo {
    height: 100%;
    padding: 0 5rem;
  }
  .panel {
    height: 100%;
    padding: 0 5rem;
    position: relative;
    &--cover {
      height: 100%;
    }
    > input {
      font-size: 2rem;
      text-align: center;
      background: rgba(0, 0, 0, 0);
      position: absolute;
      transform: translate(-50%, -50%);
      top: 49%;
      left: 50%;
      width: 40%;
      height: 12%;
      border: 0px;
      :focus {
        outline: none;
        border: 0px;
      }
    }
    .wrong-password-notice {
      position: absolute;
      transform: translate(-50%, -50%);
      top: 60%;
      left: 50%;
      color: red;
    }
    .confirm-button {
      position: absolute;
      transform: translate(-50%, -50%);
      top: 75%;
      left: 70%;
      width: 8rem;
      cursor: pointer;
    }
  }
  .loading-notice {
    color: #fff;
    font-size: 1.6rem;
    line-height: 2;
    text-align: center;
    padding: 0 5rem;
  }
`;

/**
 * 網址上帶著密碼進來嗎？這件事要在「第一次 render 之前」就知道，
 * 否則密碼輸入框會先畫出來、等 effect 跑完才被換掉，學生會看到閃一下。
 */
const hasPasswordInHash = () => {
  try {
    return /[#&]p=/.test(window.location.hash || "");
  } catch (e) {
    return false;
  }
};

const PasswordInput = ({ urlKey, handleLoading }) => {
  const [password, setPassword] = useState("");
  const [showError, setShowError] = useState(false);
  // 用 lazy initializer，首次 render 就已經是載入畫面
  const [autoEntering, setAutoEntering] = useState(hasPasswordInHash);
  const autoTried = useRef(false);

  const handleChange = (e) => {
    setPassword(e.target.value);
  };

  // useEffect(() => {
  //   const url = process.env.REACT_APP_GET_EXAM_BY_URLKEY + "/" + urlKey;
  //   axios.post(`${url}`, { password: "EIMNU5U4" }).then((res) => {
  //     const data = res.data;
  //     if (data.success) {
  //       console.log(data);
  //       handleLoading(
  //         {
  //           ...data.candidate,
  //         },
  //         {
  //           ...data.practice_test,
  //           pages: JSON.parse(data.practice_test.pages),
  //           answers: JSON.parse(data.practice_test.answers),
  //         }
  //       );
  //     } else {
  //       setShowError(true);
  //     }
  //   });
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [urlKey]);

  /**
   * 自動帶入失敗時要退回手動輸入的畫面。
   * 少了這一步，學生會永遠停在「載入題庫中」，連錯誤訊息都看不到。
   */
  const fallBackToManual = () => {
    setShowError(true);
    setAutoEntering(false);
  };

  // 實際送出。password 由參數帶入，因為自動帶入時 state 還沒更新完
  const submit = (pw) => {
    const url = process.env.REACT_APP_GET_EXAM_BY_URLKEY + "/" + urlKey;
    axios
      .post(`${url}`, { password: pw })
      .then((res) => {
        const data = res.data;
        if (data.success) {
          handleLoading(
            {
              ...data.candidate,
            },
            {
              ...data.practice_test,
              pages: JSON.parse(data.practice_test.pages),
              answers: JSON.parse(data.practice_test.answers),
              // 續考：上次存到一半的答案與剩餘時間（沒有就是 null）
              progress: data.progress || null,
            }
          );
        } else {
          fallBackToManual();
        }
      })
      // 連線失敗原本沒有人接，畫面會靜靜地卡住
      .catch(() => fallBackToManual());
  };

  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    submit(password);
  };

  // ------------------------------------------------------------
  // 自我練習：由學習系統（hub）帶著密碼過來時直接進場，
  // 不必讓學生手動複製貼上一組 8 碼密碼。
  //
  // 密碼放在網址的 fragment（#p=xxxx）而不是查詢字串：
  // fragment 不會送到伺服器，因此不會留在 nginx 的存取紀錄裡。
  // 讀完立刻從網址移除，也不留在瀏覽器的網址列與上一頁紀錄。
  //
  // 正式模考的連結不帶 #p=，行為完全不變，仍需手動輸入密碼。
  // ------------------------------------------------------------
  useEffect(() => {
    if (autoTried.current || !urlKey) {
      return;
    }
    const hash = window.location.hash || "";
    const matched = hash.match(/[#&]p=([^&]+)/);
    if (!matched) {
      return;
    }
    autoTried.current = true;

    let pw = "";
    try {
      pw = decodeURIComponent(matched[1]);
    } catch (e) {
      pw = matched[1];
    }

    // 先把密碼從網址拿掉，再送出——即使後續請求失敗，網址上也不會殘留
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );

    if (pw) {
      setPassword(pw);
      submit(pw);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey]);

  // 從學習系統帶密碼進來的情況：不要讓學生看到一個他填不了的密碼框
  if (autoEntering) {
    return (
      <div className="main-content-container">
        <StyledDiv>
          <img className="logo" src={WhiteLogo} alt="logo" />
          <div className="loading-notice">
            <p>載入題庫中，請稍候…</p>
            <p>Loading your test…</p>
          </div>
        </StyledDiv>
      </div>
    );
  }

  return (
    <div className="main-content-container">
      <StyledDiv>
        <img className="logo" src={WhiteLogo} alt="logo" />
        <div className="panel">
          <img className="panel--cover" src={Panel} alt="panel" />
          <input
            type="password"
            name="password"
            value={password}
            onChange={handleChange}
          />
          {showError && (
            <div className="wrong-password-notice">
              <i>Wrong Password</i>
            </div>
          )}
          <img
            className="confirm-button"
            src={ConfirmButton}
            alt="confirm-button"
            onClick={handleSubmit}
          />
        </div>
      </StyledDiv>
      <form className="password-form" onSubmit={handleSubmit}>
        <input type="password" name="password" onChange={handleChange} />
        {showError && (
          <div className="wrong-password-notice">
            <i>Wrong Password</i>
          </div>
        )}
        <div>
          <button type="submit">Enter Exam</button>
        </div>
      </form>
    </div>
  );
};

export default PasswordInput;
